import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminLogin = ({ language = 'ar', onLoginSuccess }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isRTL = language === 'ar';

  const t = {
    ar: {
      title: 'لوحة التحكم',
      subtitle: 'سيرينا بيوتي سنتر',
      password: 'كلمة المرور',
      login: 'دخول',
      error: 'كلمة المرور غير صحيحة',
      placeholder: 'أدخلي كلمة المرور',
    },
    en: {
      title: 'Admin Panel',
      subtitle: 'Sirena Beauty Center',
      password: 'Password',
      login: 'Login',
      error: 'Invalid password',
      placeholder: 'Enter password',
    },
  };

  const text = t[language];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        // Store login state in sessionStorage
        sessionStorage.setItem('adminLoggedIn', 'true');
        onLoginSuccess();
      }
    } catch (err) {
      setError(text.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className={`min-h-screen flex items-center justify-center p-4 ${isRTL ? 'rtl' : 'ltr'}`}
      style={{ 
        background: 'linear-gradient(135deg, #f8f6f0 0%, #e8f4f8 50%, #f0e8e0 100%)'
      }}
      data-testid="admin-login"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_rose-gold-salon-2/artifacts/0gqiaw1n_sirensa%201.jpeg"
              alt="Sirena Beauty Center"
              className="w-32 h-auto mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-[#4A4A4A] heading">
              {text.title}
            </h1>
            <p className="text-[#7D7D7D] text-sm mt-1">
              {text.subtitle}
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="password" className="text-[#4A4A4A]">
                {text.password}
              </Label>
              <div className="relative mt-2">
                <div className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'right-3' : 'left-3'}`}>
                  <Lock className="w-5 h-5 text-[#7D7D7D]" />
                </div>
                <Input
                  id="password"
                  data-testid="admin-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={text.placeholder}
                  className={`${isRTL ? 'pr-10 pl-10' : 'pl-10 pr-10'} h-12`}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-3' : 'right-3'} text-[#7D7D7D] hover:text-[#4A4A4A]`}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 text-red-500 text-sm bg-red-50 p-3 rounded-lg"
              >
                <AlertCircle className="w-4 h-4" />
                {error}
              </motion.div>
            )}

            <Button
              type="submit"
              data-testid="admin-login-btn"
              disabled={loading || !password}
              className="w-full h-12 bg-[#C9A050] hover:bg-[#B8903F] text-white rounded-xl font-semibold"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                text.login
              )}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};
